/** @jsxImportSource theme-ui */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, List } from 'semantic-ui-react';
import Shiitake from 'shiitake';

import { getRiskLikelihood, getRiskMagnitude } from 'utils/modelHelpers';
import { formatNumberOfCases } from 'utils/stringFormattingHelpers';

import { FlexGroup } from 'components/_common/FlexGroup';
import { BdImportationRiskIcon } from 'components/_common/Icon/BdImportationRiskIcon';
import { Typography } from 'components/_common/Typography';
import { BdTooltip } from 'components/_controls/BdTooltip';
import { OutbreakCategoryStandAlone } from 'components/OutbreakCategory';

import diseaseActivitiesInLocationSelectors from 'diseaseActivitiesInLocation/diseaseActivitiesInLocation.selectors';
import { SparkGraphProximal } from 'diseaseActivitiesInLocation/SparkGraphProximal';
import { openPanel } from 'diseaseActivityPanels/diseaseActivityPanels.saga';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';
import locationsSelectors from 'locations/locations.selectors';

import { diseaseCardStyle } from './diseases.styles';
import { RiskDisplayTooltip } from './RiskDisplayTooltip';

type DiseaseInLocationCardProps = {
  diseaseId: number;
};
const DiseaseInLocationCard: React.FC<DiseaseInLocationCardProps> = ({ diseaseId }) => {
  const dispatch = useDispatch();
  const diseaseName = useSelector(diseaseActivitiesInLocationSelectors.getDiseaseName(diseaseId));
  const outbreakPotentialCategory = useSelector(
    diseaseActivitiesInLocationSelectors.getOutbreakPotentialCategory(diseaseId)
  );
  const riskModel = useSelector(diseaseActivitiesInLocationSelectors.getRiskModel(diseaseId));
  const nearbyReportedCases = useSelector(
    diseaseActivitiesInLocationSelectors.getProximalReportedCases(diseaseId)
  );
  const isSelected = useSelector(diseaseActivityPanelsSelectors.isDiseaseSelected(diseaseId));
  const selectedLocationTitle = useSelector(locationsSelectors.getSelectedLocationTitle);

  const riskMagnitude = getRiskMagnitude(riskModel);
  const riskLikelihood = getRiskLikelihood(riskModel);
  return (
    <List.Item
      data-diseaseid={diseaseId}
      sx={diseaseCardStyle}
      active={isSelected}
      onClick={e => dispatch(openPanel({ panel: 'DiseaseActivityList', diseaseId: diseaseId }))}
    >
      <List.Content>
        <List.Header sx={{ mb: '14px' }}>
          <FlexGroup
            suffix={
              <BdTooltip
                customPopup={
                  <RiskDisplayTooltip
                    riskLikelihood={riskLikelihood}
                    riskMagnitude={riskMagnitude}
                  />
                }
                position="left center"
                wide
              >
                <BdImportationRiskIcon
                  riskMagnitude={riskMagnitude}
                  riskLikelihood={riskLikelihood}
                />
              </BdTooltip>
            }
            alignItems="center"
          >
            <Typography variant="subtitle2" color="stone90" marginBottom="4px">
              {diseaseName}
            </Typography>
            <OutbreakCategoryStandAlone outbreakPotentialCategory={outbreakPotentialCategory} />
          </FlexGroup>
        </List.Header>
        <List.Description>
          <Grid
            columns="equal"
            relaxed="very"
            sx={{
              '.icon.bd-icon': {
                fontSize: '14px',
              },
            }}
          >
            <Grid.Row>
              <Grid.Column className="override-semantic-column-flex-grow1">
                <SparkGraphProximal diseaseId={diseaseId} />
              </Grid.Column>
              <Grid.Column className="override-semantic-column-flex-grow2">
                <Typography variant="caption" color="deepSea50" marginBottom="2px">
                  <Shiitake lines={2} throttleRate={50} className="shiitake">
                    {`Cases reported in ${selectedLocationTitle}`}
                  </Shiitake>
                </Typography>
                <Typography variant="subtitle2" color="stone90" marginBottom="8px">
                  {formatNumberOfCases(nearbyReportedCases)}
                </Typography>
                <Typography variant="caption" color="deepSea50" marginBottom="2px" nowrap>
                  Estimated case importations per month
                </Typography>
                <Typography variant="subtitle2" color="stone90" marginBottom="0">
                  {riskMagnitude}
                </Typography>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </List.Description>
      </List.Content>
    </List.Item>
  );
};
export default DiseaseInLocationCard;
